import { useState } from 'react';

export type UseTableProps = {
  dense: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: number;
  order: 'asc' | 'desc';
  orderBy: string;
  setOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  //
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  onSelectRow: (id: string) => void;
  onSelectAllRows: (checked: boolean, newSelecteds: string[]) => void;
  //
  onSort: (id: string) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDense: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type Props = {
  defaultDense?: boolean;
  defaultOrder?: 'asc' | 'desc';
  defaultOrderBy?: string;
  defaultSelected?: string[];
  defaultRowsPerPage?: number;
  defaultCurrentPage?: number;
  rowsPerPageStorageKey?: string;
};

const getStoredRowsPerPage = (
  storageKey: string | undefined,
  fallback: number
) => {
  if (!storageKey) return fallback;

  try {
    const storedValue = localStorage.getItem(storageKey);
    if (storedValue === null) return fallback;

    const parsedValue = Number(JSON.parse(storedValue));
    return Number.isFinite(parsedValue) && parsedValue > 0
      ? parsedValue
      : fallback;
  } catch {
    return fallback;
  }
};

const setStoredRowsPerPage = (
  storageKey: string | undefined,
  rowsPerPage: number
) => {
  if (!storageKey || !Number.isFinite(rowsPerPage) || rowsPerPage <= 0) return;

  try {
    localStorage.setItem(storageKey, JSON.stringify(rowsPerPage));
  } catch {
    // Ignore storage errors so table pagination still works normally.
  }
};

export default function useTable(props?: Props) {
  const [dense, setDense] = useState(props?.defaultDense || false);

  const [orderBy, setOrderBy] = useState(props?.defaultOrderBy || 'name');

  const [order, setOrder] = useState<'asc' | 'desc'>(
    props?.defaultOrder || 'asc'
  );

  const [page, setPage] = useState(props?.defaultCurrentPage || 0);

  const defaultRowsPerPage = props?.defaultRowsPerPage || 25;

  const [rowsPerPage, setRowsPerPage] = useState(() =>
    getStoredRowsPerPage(props?.rowsPerPageStorageKey, defaultRowsPerPage)
  );

  const [selected, setSelected] = useState<string[]>(
    props?.defaultSelected || []
  );

  const onSort = (id: string) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };

  const onSelectRow = (id: string) => {
    const selectedIndex = selected.indexOf(id);

    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const onSelectAllRows = (checked: boolean, newSelecteds: string[]) => {
    if (checked) {
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const onChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(nextRowsPerPage);
    setStoredRowsPerPage(props?.rowsPerPageStorageKey, nextRowsPerPage);
    setPage(0);
  };

  const onChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  return {
    dense,
    order,
    page,
    setPage,
    setOrder,
    setOrderBy,
    orderBy,
    rowsPerPage,
    //
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangePage,
    onChangeDense,
    onChangeRowsPerPage,
  };
}

// ----------------------------------------------------------------------

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const flattenObject = (obj: unknown, parentKey?: string) => {
    let result: { [key: string]: any } = {};
    if (typeof obj === 'object' && obj !== null) {
      Object.keys(obj).forEach((key) => {
        // @ts-ignore
        const value = obj[key];
        // TODO: add case for array
        // Array.isArray(value) && console.log(value);
        const _key = parentKey ? parentKey + '.' + key : key;
        if (typeof value === 'object' && value !== null) {
          result = { ...result, ...flattenObject(value, _key) };
        } else {
          result[_key] = value;
        }
      });
    }
    return result;
  };

  const flattenedA = flattenObject(a);
  const flattenedB = flattenObject(b);
  const A = flattenedA[orderBy as keyof typeof flattenedA] ?? '';
  const B = flattenedB[orderBy as keyof typeof flattenedB] ?? '';

  if (B < A) {
    return -1;
  }
  if (B > A) {
    return 1;
  }
  return 0;
}

export function getComparator<T>(
  order: 'asc' | 'desc',
  orderBy: keyof T
): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function emptyRows(
  page: number,
  rowsPerPage: number,
  arrayLength: number
) {
  return page > 0 ? Math.max(0, (1 + page) * rowsPerPage - arrayLength) : 0;
}
